import { FC, useMemo } from "react";

import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import { useLocalization } from "@/hooks";
import { QuestionResults, Section } from "@/types/resources";
import MonadicReport, { MonadicReportData } from "./MonadicReport";
import { cookTopBoxScoring } from "./_topBoxScoring";

interface MonadicSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/**
 * Templated monadic sections always carry the same five-question
 * scoring battery in a fixed order. We supply the translated metric
 * labels here (rather than reading `r.question.text`) so the heatmap
 * rows are localised consistently and never echo whatever the author
 * may have typed into the question. Concept names still come from
 * `section.stimuli`.
 */
const MonadicSectionResults: FC<MonadicSectionResultsProps> = ({
  data,
  section,
}) => {
  const { i18n } = useLocalization();
  const labels = useUseCaseLabels(section.use_case);

  const reportData: MonadicReportData = useMemo(() => {
    const metricLabels = [
      i18n.t("useCasePage.monadic.sampleReport.metrics.purchaseIntent"),
      i18n.t("useCasePage.monadic.sampleReport.metrics.uniqueness"),
      i18n.t("useCasePage.monadic.sampleReport.metrics.relevance"),
      i18n.t("useCasePage.monadic.sampleReport.metrics.valueForMoney"),
      i18n.t("useCasePage.monadic.sampleReport.metrics.recommend"),
    ];
    const scored = cookTopBoxScoring(data, section, metricLabels, (i) =>
      labels.stimulusPlaceholder(i + 1)
    );
    return {
      concepts: scored.entities,
      metrics: scored.metrics,
      perCellSampleSize: scored.perCellSampleSize,
      respondentCount: scored.respondentCount,
      topBoxCounts: scored.topBoxCounts,
    };
  }, [data, section, i18n, labels]);

  return <MonadicReport data={reportData} />;
};

export default MonadicSectionResults;
